import * as React from "react";
import { ILookupDictionary } from "../../lookup/types/ILookupDictionary";
import { IActiveAccordion } from "../../redux/types/IActiveAccordion";
import IMembersPortalComponentDataItem from "../../redux/types/IMembersPortalComponentDataItem";
import { MemberPortalSearchType } from "../../redux/types/MemberPortalSearchType";
import { IResultsPerPage } from "../../redux/types/IResultsPerPage";
import {
    SEARCH_VIEW_ALL_REGISTERED_CUE_SHEETS,
    SEARCH_VIEW_DRAFT_CUE_SHEETS,
    SEARCH_VIEW_REGISTERED_CUE_SHEETS,
} from "../Consts";
import { ICueSheetsSearchQuery } from "../types/ICueSheetsSearchQuery";
import { useMsal } from "@azure/msal-react";
import { ComponentsHelper } from "../../core/services/ComponentHelper";
import { ICueSheetsSearchResult } from "../types/ICueSheetsSearchResult";
import { setDataSource } from "../../redux/reducers/MembersPortalReducer";
import { ITabReduxItem } from "../../redux/types/ITabReduxItem";
import { DataSource } from "../../repertoire/types/DataSource";
import { MemberPortalSearchTypeItem } from "../../redux/types/MemberPortalSearchTypeItem";
import CueSheetsSearchView from "./components/CueSheetsSearchView";
import { SearchRequests } from "../services/SearchRequests";
import { CONFIGURATION_PARAMETER_FIELD_FORMAT_KEY, PRODUCT_MAINTENANCE_GROUP } from "../ConfigurationConsts";
import { FormatFields } from "../../redux/types/FormatFields";
import { IMembersPortalPathInfo } from "../types/IMembersPortalPathInfo";
import InfoToggle from "../sharedComponents/button/InfoToggle";
import { AddToPackageProvider } from "../../repertoire/components/addToPackageButton/AddToPackageContext";
import { EntityType } from "../../repertoire/types/EntityType";
import { IMyPackagesDetails } from "../../repertoire/types/IMyPackagesDetails";
import { IAddtoPackageResult } from "../../repertoire/types/IAddtoPackageResult";
import { CURRENT_USER_EMAIL_KEY } from "../../redux/services/AccountSessionStorage";
import {  hideModal, showYesNoPrompt } from "../../redux/reducers/ModalReducer";

export interface IRegisteredCueSheetsPageProps {
    registeredCueSheetsMaintenanceGeneralDataView: IMembersPortalComponentDataItem;
    cueSheetsPageData?: IMembersPortalComponentDataItem;
    searchViewData: IMembersPortalComponentDataItem;
    searchResultsTableData: IMembersPortalComponentDataItem;
    searchCueSheets?: (query: ICueSheetsSearchQuery) => void;
    cueSheetsSearchResults?: ICueSheetsSearchResult[];
    memberCueSheetsSearchResults?: ICueSheetsSearchResult[];
    searchSuccessful: boolean;
    expandedProductResults: number[];
    expandAll: boolean;
    expandProductResult: (productResult: number) => void;
    expandAllResults: () => void;
    lookupValues: ILookupDictionary;
    indexOfFirstResult?: number;
    indexOfLastResult?: number;
    resultsPerPage?: number;
    currentPage?: number;
    resetPagination: (repertoireSection: string) => void;
    updatePagination: (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => void;
    sortSearchResults: (name: string, results: any) => void;
    defaultActiveAccordions?: IActiveAccordion[];
    allResultsPerPage?: IResultsPerPage[];
    updateUserPreferences: (allResultsPerPage: IResultsPerPage[], activeAccordions: IActiveAccordion[], newActiveAccordionName?: string, accordionExpanded?: boolean, componentName?: string, indexOfFirstResult?: number, indexOfLastResult?: number, resultsPerPage?: number, repertoireSection?: string) => void;
    disabled?: boolean;
    setDataSource: typeof setDataSource;
    dataSource: string;
    loadMembersPortalFromPath?: (pathInfo: IMembersPortalPathInfo, currentUserNameNumber: string, location: Location, tabs?: ITabReduxItem[], lookupValues?: ILookupDictionary) => void;
    activeTab?: number;
    tabs?: ITabReduxItem[];
    currentUserNameNumber?: string;
    currentUserIpBaseNumber?: string;
    memberPortalSearchType: MemberPortalSearchTypeItem;
    getCueSheetsDetails: (dataSource: string, productCoreId: number, lookupValues: ILookupDictionary, formats?: FormatFields[]) => void;
    myPackagesResult?: IMyPackagesDetails[] | { data?: IMyPackagesDetails[]; };
    addToPackages?: (userName: string, entityType: string, entityIds: number[]) => Promise<IAddtoPackageResult>;
    removeFromPackages?: (userName: string, entityType: string, entityIds: number[]) => Promise<IAddtoPackageResult>;
    getMyPackagesDetails?: (userName: string) => void;
    showYesNoPrompt?: typeof showYesNoPrompt;
    hideModal?: typeof hideModal;
    roles?: string[];
    fullCataloguePackagePending?: boolean;
    saveInProgress?: boolean;
    packageOperationInProgress?: boolean;
    createCueSheetPackageFromCriteria?: (userName: string, criteria: ICueSheetsSearchQuery) => Promise<IAddtoPackageResult>;
    enablePackageActions?: boolean;
}

const RegisteredCueSheetsPage = (props: IRegisteredCueSheetsPageProps) => {
    const [loaded, setLoaded] = React.useState(false);
    const { instance, accounts } = useMsal();

    const packageActionsEnabled = props.enablePackageActions !== false;

    React.useEffect(() => {
        checkIfLoaded();

        const account = accounts[0]
        ComponentsHelper.createBearerHeaderFromAADLogin(instance, account);
    }, [])

    React.useEffect(() => {
        checkIfLoaded();
    }, [props, loaded])

    React.useEffect(() => {
        if (!packageActionsEnabled || props.memberPortalSearchType.code === MemberPortalSearchType.MyDraftSearch.code)
        {
            return;
        }

        const userName = sessionStorage.getItem(CURRENT_USER_EMAIL_KEY);

        if (userName) {
            props.getMyPackagesDetails?.(userName);
        }
    }, [packageActionsEnabled, props.getMyPackagesDetails, props.memberPortalSearchType.code]);

    const checkIfLoaded = () => {
        const { cueSheetsPageData } = props;

        if (cueSheetsPageData.fields && cueSheetsPageData.fields.length >= 1 && !loaded) {
            setLoaded(true);
            const header = cueSheetsPageData.fields.find(f => f.name === "PageTitle");
            if (header) {
                document.title = header.data;
            }
        }
    }

    const updateUserPagination = (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => {
        const { defaultActiveAccordions, allResultsPerPage, updateUserPreferences, updatePagination } = props;
        updatePagination(indexOfFirstResult, indexOfLastResult, resultsPerPage, currentPage, repertoireSection);
        updateUserPreferences(allResultsPerPage, defaultActiveAccordions, undefined, undefined, undefined, indexOfFirstResult, indexOfLastResult, resultsPerPage, repertoireSection);
    }

    const typeToDataSourceMap: Record<string, DataSource> = {
        [MemberPortalSearchType.MyDraftSearch.code]: DataSource.Intray,
        [MemberPortalSearchType.MyRegisteredSearch.code]: DataSource.Repertoire,
        [MemberPortalSearchType.AllRegisteredSearch.code]: DataSource.Repertoire,
    };

    const currentDataSource = typeToDataSourceMap[props.memberPortalSearchType.code] ?? '';
    setDataSource(currentDataSource);

    const packageResults: IMyPackagesDetails[] = packageActionsEnabled
        ? Array.isArray(props.myPackagesResult)
            ? props.myPackagesResult
            : Array.isArray((props.myPackagesResult as any)?.data)
                ? (props.myPackagesResult as any).data
                : []
        : [];

    const openProductPackage = packageResults.find(
        packageItem =>
            packageItem.entityType === EntityType.Products &&
            packageItem.status === "Open"
    );

    const packageActionsDisabled = !packageActionsEnabled || Boolean(openProductPackage?.searchCriteriaJson) || Boolean(props.packageOperationInProgress);

    const fullCatalogueCreationDisabled =
        Boolean(props.packageOperationInProgress) ||
        Boolean(
            openProductPackage &&
            (
                Boolean(openProductPackage.searchCriteriaJson) ||
                Boolean(openProductPackage.ipBaseNumber) ||
                (openProductPackage.packageItems?.length ?? 0) > 0
            )
        );

    const productPackageItems = packageActionsDisabled ? [] : openProductPackage?.packageItems ?? [];

    if (loaded) {
        const {
            cueSheetsPageData,
            searchViewData,
            searchResultsTableData,
            searchCueSheets,
            searchSuccessful,
            indexOfFirstResult,
            indexOfLastResult,
            resultsPerPage,
            currentPage,
            resetPagination,
            sortSearchResults,
            lookupValues,
            currentUserNameNumber,
            currentUserIpBaseNumber,
            tabs,
            activeTab,
            expandAll,
            loadMembersPortalFromPath,
            expandProductResult,
            expandAllResults,
            expandedProductResults
        } = props;


        let searchPageLabelTitle = null;
        if (cueSheetsPageData && cueSheetsPageData.fields.length > 0) {
            searchPageLabelTitle = cueSheetsPageData.fields.find((v, i, a) => v.name == "searchPageLabelTitle").data;
        }

        const searchResults = props.memberCueSheetsSearchResults;
        
        const getCueSheetsDetails = (dataSource: string, productCoreId: number) => {
            SearchRequests.getConfigurationParameter(CONFIGURATION_PARAMETER_FIELD_FORMAT_KEY, PRODUCT_MAINTENANCE_GROUP)
                .then(formats => {
                    props.getCueSheetsDetails(dataSource, productCoreId, lookupValues, formats);
                });
        }


        const infoToggleHeaderField = cueSheetsPageData.fields
            .find(x => x.name === 'infoToggleHeader');

        const infoToggleHtmlField = cueSheetsPageData.fields
            .find(x => x.name === 'infoToggleHTML');


        const renderInfoToggle = (
            <InfoToggle
                headerTextItem={infoToggleHeaderField}
                htmlTextItem={infoToggleHtmlField}
                currentUserNameNumber={currentUserNameNumber}
                lookupValues={lookupValues}
                loadMembersPortalFromPath={loadMembersPortalFromPath}
            />
        )

        const cueSheetsPaginationSection = props.memberPortalSearchType.code === MemberPortalSearchType.MyDraftSearch.code ? SEARCH_VIEW_DRAFT_CUE_SHEETS : props.memberPortalSearchType.code === MemberPortalSearchType.AllRegisteredSearch.code ? SEARCH_VIEW_ALL_REGISTERED_CUE_SHEETS: SEARCH_VIEW_REGISTERED_CUE_SHEETS;

        return (
            <div>
                <div className="row">
                    <div className="col-md-12">
                        <span className="title">{searchPageLabelTitle}</span>
                        {renderInfoToggle}
                    </div>
                </div>
                <div>
                    
                </div>
                <AddToPackageProvider initialAddedIds={productPackageItems}>
                    <CueSheetsSearchView
                        searchViewData={searchViewData}
                        searchResultsTableData={searchResultsTableData}
                        componentInstance={SEARCH_VIEW_REGISTERED_CUE_SHEETS}
                        modalProps={undefined}
                        dataSource={currentDataSource}
                        lookupValues={lookupValues}
                        searchCueSheets={searchCueSheets}
                        cueSheetsSearchResults={searchResults}
                        searchSuccessful={searchSuccessful}
                        indexOfFirstResult={indexOfFirstResult}
                        indexOfLastResult={indexOfLastResult}
                        resultsPerPage={resultsPerPage}
                        currentPage={currentPage}
                        resetPagination={resetPagination}
                        updatePagination={updateUserPagination}
                        sortSearchResults={sortSearchResults}
                        searchDisabled={false}
                        getCueSheetsDetails={getCueSheetsDetails}
                        memberPortalSearchType={props.memberPortalSearchType}
                        currentUserNameNumber={currentUserNameNumber}
                        currentUserIpBaseNumber={currentUserIpBaseNumber}
                        tabs={tabs}
                        activeTab={activeTab}
                        expandAll={expandAll}
                        expandProductResult={expandProductResult}
                        expandAllResults={expandAllResults}
                        expandedProductResults={expandedProductResults}
                        roles={props.roles}
                        addToPackages={packageActionsEnabled ? props.addToPackages : undefined}
                        removeFromPackages={packageActionsEnabled ? props.removeFromPackages : undefined}
                        showYesNoPrompt={packageActionsEnabled ? props.showYesNoPrompt : undefined}
                        hideModal={packageActionsEnabled ? props.hideModal : undefined}
                        packageActionsDisabled={packageActionsDisabled}
                        fullCataloguePackagePending={packageActionsEnabled ? Boolean(openProductPackage?.searchCriteriaJson) : undefined}
                        fullCatalogueCreationDisabled={packageActionsEnabled ? fullCatalogueCreationDisabled : true}
                        createCueSheetPackageFromCriteria={packageActionsEnabled ? props.createCueSheetPackageFromCriteria : undefined}
                        enablePackageActions={packageActionsEnabled}
                        paginationSection={cueSheetsPaginationSection}
                    />
                </AddToPackageProvider>
            </div>
        );
    }
    return <div>Loading ...</div>
}

export default RegisteredCueSheetsPage;
